import { saveWaitingServiceWorker } from "@redux/serviceWorker";
import { store, persistor } from "@redux/store";
import * as Sentry from "@sentry/browser";
import "babel-polyfill";
import { I18n } from "aws-amplify";
import { createRoot } from "react-dom/client";

import { messages } from "components/shared/Internationlization/Internationalization";
import {
	AMPLIFY_EN_LOCALE,
	AMPLIFY_FR_LOCALE,
	FR_LOCALE,
} from "constants/locales";
import { isB2bEnvironment } from "services/helpers/featureToggle";
import { getLocale } from "services/helpers/locale";
import {
	FR_VOCABULARY,
	EN_VOCABULARY,
} from "translations/b2c/amplifyTranslations";

import App from "./App";
import AppB2b from "./AppB2b";
import AppB2c from "./AppB2c";
import { register, unregister } from "./serviceWorker";
import dayjs from "dayjs";
import objectSupport from "dayjs/plugin/objectSupport";
import duration from "dayjs/plugin/duration";
/* Language polyfills needed for IE11, Edge, Safari 12 & 13 support
   https://github.com/formatjs/react-intl/blob/master/docs/Upgrade-Guide.md#migrate-to-using-native-intl-apis
 */
import "@formatjs/intl-pluralrules/polyfill";
import "@formatjs/intl-pluralrules/dist/locale-data/en";
import "@formatjs/intl-pluralrules/dist/locale-data/fr";

import "@formatjs/intl-relativetimeformat/polyfill";
import "@formatjs/intl-relativetimeformat/dist/locale-data/en";
import "@formatjs/intl-relativetimeformat/dist/locale-data/fr";
import './index.css';

/* End of language polyfills */

declare global {
	interface Window {
		config: {
			sentry: {
				dsn: string;
				release: string;
				environment: string;
			};
		};
	}
}

declare global {
	interface HTMLVideoElement {
		audioContext?: AudioContext;
		gainNode?: GainNode;
		sourceNode?: MediaElementAudioSourceNode;
	}
}

const isB2b = isB2bEnvironment();

const SENTRY_CONFIG = {
	dsn: process.env.REACT_APP_SENTRY_DSN,
	environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
};
Sentry.init(SENTRY_CONFIG);

// Internationalization

const locale = getLocale();

dayjs.extend(objectSupport);
dayjs.extend(duration);

I18n.putVocabulariesForLanguage(AMPLIFY_EN_LOCALE, EN_VOCABULARY);
I18n.putVocabulariesForLanguage(AMPLIFY_FR_LOCALE, FR_VOCABULARY);

I18n.setLanguage(locale === FR_LOCALE ? AMPLIFY_FR_LOCALE : AMPLIFY_EN_LOCALE);

const rootEl = document.getElementById("root");
// biome-ignore lint/style/noNonNullAssertion: .
const root = createRoot(rootEl!);

if (root) {
	root.render(
		<App store={store} persistor={persistor} locale={locale}>
			{isB2b ? <AppB2b /> : <AppB2c />}
		</App>,
	);

	if (window.location.origin.includes("bam-karaoketogo")) {
		unregister();
	} else {
		register({
			onUpdate: (registration: ServiceWorkerRegistration) => {
				const waitingServiceWorker = registration.waiting;
				if (waitingServiceWorker) {
					store.dispatch(saveWaitingServiceWorker(waitingServiceWorker));
				}
			},
		});
	}
}

if (module.hot) {
	module.hot.accept("./App", () => {
		const NextApp = require("./App").default; // eslint-disable-line
		if (root) {
			root.render(
				<NextApp
					store={store}
					persistor={persistor}
					locale={locale}
					messages={messages[locale]}
				>
					{isB2b ? <AppB2b /> : <AppB2c />}
				</NextApp>,
			);
		}
	});
}
